import Tippy from '@tippyjs/react'
import {FC, useEffect, useMemo, useRef, useState} from 'react'
import {BlockNoteEditor, BlockSchema, DefaultBlockSchema, LinkMenuProsemirrorPlugin, LinkMenuState} from '../../../core'

import {LinkMenuItem} from '../../../core/extensions/LinkMenu/LinkMenuItem'
import {DefaultLinkMenu} from './DefaultLinkMenu'

export type LinkMenuProps<BSchema extends BlockSchema = DefaultBlockSchema> = Pick<
  LinkMenuProsemirrorPlugin<BSchema, any>,
  'itemCallback'
> &
  Pick<LinkMenuState<LinkMenuItem<BSchema>>, 'items' | 'keyboardHoveredItemIndex'>

export const LinkMenuPositioner = <BSchema extends BlockSchema = DefaultBlockSchema>(props: {
  editor: BlockNoteEditor<BSchema>
  linkMenu?: FC<LinkMenuProps<BSchema>>
}) => {
  const [mobileKeyboardOpen, setMobileKeyboardOpen] = useState(false)
  const [show, setShow] = useState<boolean>(false)
  const [ref, setRef] = useState<string>('')
  const [items, setItems] = useState<LinkMenuItem<BSchema>[]>([])
  const [keyboardHoveredItemIndex, setKeyboardHoveredItemIndex] = useState<number>()
  const scroller = useRef<HTMLElement | null>(null)

  const referencePos = useRef<DOMRect>()
  useEffect(() => {
    setTimeout(() => {
      scroller.current = document.getElementById('scroll-page-wrapper')
    }, 100)
  }, [])

  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return
    const onResize = () => {
      setMobileKeyboardOpen(vv.height < window.innerHeight * 0.75)
    }
    vv.addEventListener('resize', onResize)
    return () => vv.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    return props.editor.linkMenu!.onUpdate((linkMenuState) => {
      setShow(linkMenuState.show)
      // @ts-expect-error
      setRef(linkMenuState.ref)
      // @ts-ignore
      setItems(linkMenuState.items)
      setKeyboardHoveredItemIndex(linkMenuState.keyboardHoveredItemIndex)

      referencePos.current = linkMenuState.referencePos
    })
  }, [props.editor, props.editor.linkMenu!])

  const getReferenceClientRect = useMemo(
    () => {
      if (!referencePos.current) {
        return undefined
      }

      const boundingRect = referencePos.current!
      return () => boundingRect as DOMRect
    },
    [referencePos.current], // eslint-disable-line
  )

  const linkMenuElement = useMemo(
    () => {
      if (keyboardHoveredItemIndex === undefined) {
        return null
      }

      const LinkMenu = props.linkMenu || DefaultLinkMenu

      return (
        <LinkMenu
          items={items}
          itemCallback={(item) => props.editor.linkMenu!.itemCallback(item, ref)}
          keyboardHoveredItemIndex={keyboardHoveredItemIndex}
        />
      )
    },
    [keyboardHoveredItemIndex, props.editor.linkMenu!, props.linkMenu, ref, items], // eslint-disable-line
  )

  return (
    <Tippy
      // Always append to document.body so the popup escapes any container
      // stacking context.
      appendTo={document.body}
      content={linkMenuElement}
      getReferenceClientRect={getReferenceClientRect}
      interactive={true}
      visible={show}
      animation={'fade'}
      placement={mobileKeyboardOpen ? 'top-start' : 'bottom-start'}
    />
  )
}
